import React, { useState, useEffect } from "react";
import { Card, List, Typography, Button, Skeleton, Divider } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { MdDragIndicator } from "react-icons/md";
import { getProductsWeights } from "../../utils/fetchProducts";
import { fetchDrivers } from "../../utils/fetchDrivers";
import { getGroupedOrders } from "../../utils/fetchOrders";

const driversTemplate = [
  { driver: "Victor", city: "PUENTE ALTO" },
  { driver: "Victor", city: "LA FLORIDA" },
  { driver: "Victor", city: "LA PINTANA" },
  { driver: "Victor", city: "EL BOSQUE" },
  { driver: "Victor", city: "SAN BERNARDO" },
  { driver: "Victor", city: "LA CISTERNA" },
  { driver: "Victor", city: "SAN MIGUEL" },
  { driver: "Junior", city: "SANTIAGO CENTRO" },
  { driver: "Junior", city: "LA REINA" },
  { driver: "Junior", city: "PEÑALOLEN" },
  { driver: "Junior", city: "VITACURA" },
  { driver: "Junior", city: "LAS CONDES" },
  { driver: "Junior", city: "LO BARNECHEA" },
  { driver: "Junior", city: "PROVIDENCIA" },
  { driver: "Luis", city: "INDEPENDENCIA" },
  { driver: "Luis", city: "QUILICURA" },
  { driver: "Luis", city: "RECOLETA" },
  { driver: "Luis", city: "HUECHURABA" },
  { driver: "Luis", city: "CERRO NAVIA" },
  { driver: "Luis", city: "RENCA" },
  { driver: "Luis", city: "CONCHALI" },
  { driver: "Luis", city: "CONCHALÍ" },
  { driver: "Luis", city: "COLINA" },
  { driver: "Luis", city: "LO PRADO" },
  { driver: "Luis", city: "QUINTA NORMAL" },
  { driver: "Luis", city: "PUDAHUEL" },
  { driver: "Luis", city: "ESTACION CENTRAL" },
  { driver: "Luis", city: "ESTACIÓN CENTRAL" },
  { driver: "Patricio", city: "MAIPU" },
  { driver: "Patricio", city: "MAIPÚ" },
  { driver: "Patricio", city: "CERRILLOS" },
  { driver: "Patricio", city: "LO ESPEJO" },
  { driver: "Patricio", city: "PEDRO AGUIRRE CERDA" },
  { driver: "Patricio", city: "PEÑAFLOR" },
  { driver: "Patricio", city: "PADRE HURTADO" },
  { driver: "Patricio", city: "BUIN" },
  { driver: "Aldo", city: "MACUL" },
  { driver: "Aldo", city: "ÑUÑOA" },
  { driver: "Aldo", city: "SAN JOAQUIN" },
  { driver: "Aldo", city: "SAN JOAQUÍN" },
  { driver: "Aldo", city: "SAN RAMON" },
  { driver: "Aldo", city: "SAN RAMÓN" },
  { driver: "Aldo", city: "LA GRANJA" },
];

const driversTemplateMayorista = [
  { driver: "Empresa", city: "LA FLORIDA" }, { driver: "Empresa", city: "LA PINTANA" },
  { driver: "Empresa", city: "PUENTE ALTO" }, { driver: "Empresa", city: "SAN BERNARDO" },
  { driver: "Empresa", city: "LA REINA" }, { driver: "Empresa", city: "LAS CONDES" },
  { driver: "Empresa", city: "PROVIDENCIA" }, { driver: "Empresa", city: "SANTIAGO CENTRO" },
  { driver: "Empresa", city: "VITACURA" }, { driver: "Empresa", city: "LO BARNECHEA" },
  { driver: "Empresa", city: "COLINA" }, { driver: "Empresa", city: "CERRO NAVIA" },
  { driver: "Empresa", city: "CONCHALI" }, { driver: "Empresa", city: "CONCHALÍ" },
  { driver: "Empresa", city: "HUECHURABA" }, { driver: "Empresa", city: "INDEPENDENCIA" },
  { driver: "Empresa", city: "QUILICURA" }, { driver: "Empresa", city: "RECOLETA" },
  { driver: "Empresa", city: "RENCA" }, { driver: "Empresa", city: "CERRILLOS" },
  { driver: "Empresa", city: "ESTACION CENTRAL" }, { driver: "Empresa", city: "ESTACIÓN CENTRAL" },
  { driver: "Empresa", city: "LO PRADO" }, { driver: "Empresa", city: "MAIPU" },
  { driver: "Empresa", city: "MAIPÚ" }, { driver: "Empresa", city: "PUDAHUEL" },
  { driver: "Empresa", city: "QUINTA NORMAL" }, { driver: "Empresa", city: "EL BOSQUE" },
  { driver: "Empresa", city: "LA GRANJA" }, { driver: "Empresa", city: "PEDRO AGUIRRE CERDA" },
  { driver: "Empresa", city: "SAN JOAQUIN" }, { driver: "Empresa", city: "SAN JOAQUÍN" },
  { driver: "Empresa", city: "SAN MIGUEL" }, { driver: "Empresa", city: "SAN RAMON" },
  { driver: "Empresa", city: "SAN RAMÓN" }, { driver: "Empresa", city: "LA CISTERNA" },
  { driver: "Empresa", city: "LO ESPEJO" }, { driver: "Empresa", city: "MACUL" },
  { driver: "Empresa", city: "ÑUÑOA" }, { driver: "Empresa", city: "PEÑALOLEN" },
  { driver: "Empresa", city: "PEÑAFLOR" }, { driver: "Empresa", city: "PADRE HURTADO" },
  { driver: "Empresa", city: "BUIN" }, { driver: "Empresa", city: "LAMPA" },
];

const driversTemplateExternal = driversTemplateMayorista.map((t) => ({
  ...t,
  driver: "ZM",
}));

const MAX_INTERNAL_ORDERS = 400;

const addWeights = (orders, weights) =>
  orders.map((o) => ({
    ...o,
    totalWeight: o.products?.reduce(
      (sum, p) => sum + (weights[p.product_id] || 0) * p.quantity,
      0
    ) ?? 0,
  }));

const onDragEnd = (result, data, setData) => {
  const { destination, source, draggableId } = result;
  if (!destination) return;
  if (
    destination.droppableId === source.droppableId &&
    destination.index === source.index
  )
    return;

  const start = data.groups[source.droppableId];
  const finish = data.groups[destination.droppableId];
  const draggedCard = start.cards.find((c) => c.id === draggableId);

  if (start === finish) {
    const newCards = Array.from(start.cards);
    newCards.splice(source.index, 1);
    newCards.splice(destination.index, 0, draggedCard);
    setData({ ...data, groups: { ...data.groups, [start.id]: { ...start, cards: newCards } } });
    return;
  }

  const startCards = Array.from(start.cards);
  startCards.splice(source.index, 1);
  const finishCards = Array.from(finish.cards);
  finishCards.splice(destination.index, 0, draggedCard);
  setData({
    ...data,
    groups: {
      ...data.groups,
      [start.id]: { ...start, cards: startCards },
      [finish.id]: { ...finish, cards: finishCards },
    },
  });
};

const DriverBoard = ({ data, setData, label, orderCount }) => {
  const groupSum = (group) =>
    group?.cards?.reduce((t, c) => t + c.number, 0);

  return (
    <div>
      <div className="flex items-baseline gap-2 my-3">
        <Typography.Text strong className="text-base">
          {label}
        </Typography.Text>
        <Typography.Text type="secondary" className="text-sm">
          {orderCount} pedidos
        </Typography.Text>
      </div>
      <DragDropContext onDragEnd={(r) => onDragEnd(r, data, setData)}>
        <div className="flex flex-wrap gap-2">
          {data.groupOrder?.map((groupId) => {
            const group = data.groups[groupId];
            return (
              <Droppable droppableId={group.id} key={group.id}>
                {(provided) => (
                  <Card
                    size="small"
                    className="w-52 shrink-0"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    title={
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">{group.title}</span>
                        <Typography.Text strong>{groupSum(group)}</Typography.Text>
                      </div>
                    }
                  >
                    <List size="small" split={false}>
                      {group.cards.map((card, index) => (
                        <Draggable draggableId={card.id} index={index} key={card.id}>
                          {(provided) => (
                            <List.Item
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="flex items-center gap-1 px-1 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <MdDragIndicator className="opacity-30 shrink-0" size={18} />
                              <div className="flex justify-between w-full">
                                <Typography.Text className="text-sm">{card.content}</Typography.Text>
                                <Typography.Text strong className="text-sm">{card.number}</Typography.Text>
                              </div>
                            </List.Item>
                          )}
                        </Draggable>
                      ))}
                    </List>
                    {group.cards.length === 0 && (
                      <Typography.Text type="secondary" className="text-xs italic block text-center py-1">
                        Sin pedidos
                      </Typography.Text>
                    )}
                    {provided.placeholder}
                  </Card>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
};

const RouteStep3 = ({ orders, onBack, onComplete, isFinishing }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [processError, setProcessError] = useState(null);
  const [groupData, setGroupData] = useState({});
  const [groupDataMayorista, setGroupDataMayorista] = useState({});
  const [groupDataExternal, setGroupDataExternal] = useState({});
  const [ordersMinorista, setOrdersMinorista] = useState([]);
  const [ordersMayorista, setOrdersMayorista] = useState([]);
  const [ordersExternal, setOrdersExternal] = useState(null);
  const [drivers, setDrivers] = useState([]);

  const process = async () => {
    setIsLoading(true);
    setProcessError(null);
    try {
      const [weights, fetchedDrivers] = await Promise.all([
        getProductsWeights(),
        fetchDrivers(),
      ]);
      setDrivers(fetchedDrivers);

      const weighted = addWeights(orders, weights);
      const minorista = weighted.filter((o) => o.totalWeight <= 100);
      const mayorista = weighted.filter((o) => o.totalWeight > 100);

      const possibleExternal = minorista.filter(
        (o) =>
          o.totalWeight <= 11 &&
          ["Efectivo", "Transferencia"].includes(
            o.payment_info_array?.[0]?.payment_method
          )
      );

      let finalMinorista = minorista;
      let finalExternal = null;

      if (minorista.length > MAX_INTERNAL_ORDERS) {
        finalExternal = possibleExternal.slice(
          0,
          minorista.length - MAX_INTERNAL_ORDERS
        );
        finalMinorista = minorista.filter((o) => !finalExternal.includes(o));
      }

      setOrdersMinorista(finalMinorista);
      setOrdersMayorista(mayorista);
      setOrdersExternal(finalExternal);
      setGroupData(getGroupedOrders(finalMinorista, driversTemplate));
      setGroupDataMayorista(getGroupedOrders(mayorista, driversTemplateMayorista));
      if (finalExternal)
        setGroupDataExternal(getGroupedOrders(finalExternal, driversTemplateExternal));
    } catch (err) {
      setProcessError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    process();
  }, [orders]);

  const handleTerminar = () => {
    onComplete({
      groupData,
      groupDataMayorista,
      groupDataExternal,
      data: orders,
      drivers,
      ordersMinorista,
      ordersMayorista,
      ordersExternal,
    });
  };

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;

  if (processError)
    return (
      <div>
        <div className="text-red-500 mb-4">
          Error al cargar pesos o choferes: {processError.message}
        </div>
        <div className="flex justify-between">
          <Button onClick={onBack}>Volver</Button>
          <Button icon={<SyncOutlined />} onClick={process}>Reintentar</Button>
        </div>
      </div>
    );

  return (
    <div>
      <div className="flex justify-end mb-2">
        <Button icon={<SyncOutlined />} onClick={process} size="small">
          Recalcular
        </Button>
      </div>

      <DriverBoard
        data={groupData}
        setData={setGroupData}
        label="Minoristas (≤ 100 kg)"
        orderCount={ordersMinorista.length}
      />

      {ordersExternal?.length > 0 && (
        <>
          <Divider />
          <DriverBoard
            data={groupDataExternal}
            setData={setGroupDataExternal}
            label="Externos (ZM)"
            orderCount={ordersExternal.length}
          />
        </>
      )}

      <Divider />
      <DriverBoard
        data={groupDataMayorista}
        setData={setGroupDataMayorista}
        label="Mayoristas (> 100 kg)"
        orderCount={ordersMayorista.length}
      />

      <div className="flex justify-between mt-6">
        <Button onClick={onBack}>Volver</Button>
        <Button type="primary" onClick={handleTerminar} loading={isFinishing}>
          Terminar
        </Button>
      </div>
    </div>
  );
};

export default RouteStep3;
